import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import {
  MagnifyingGlass, ListChecks, ChatsCircle, Receipt, FileText, X,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent,
} from "./ui/dialog";

export default function GlobalSearch() {
  const { activeCompanyId } = useCompany();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  // Keyboard shortcut: "/" or Ctrl/Cmd+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !/input|textarea/i.test(e.target?.tagName))) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const run = useCallback(async (query) => {
    if (!query.trim() || !activeCompanyId) { setResults(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/search?company_id=${activeCompanyId}&q=${encodeURIComponent(query)}`);
      setResults(data);
    } catch (e) { setResults(null); }
    setLoading(false);
  }, [activeCompanyId]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults(null); return; }
    timer.current = setTimeout(() => run(q), 250);
  }, [q, run]);

  const goto = (path) => { setOpen(false); setQ(""); setResults(null); nav(path); };

  const total = results ? (results.monthly_items.length + results.inquiries.length + results.remittances.length + results.documents.length) : 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="search-trigger"
        className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-all text-sm text-[#525252] w-10 lg:w-64 justify-center lg:justify-between"
        title="Search"
      >
        <span className="flex items-center gap-2">
          <MagnifyingGlass size={16} weight="duotone" />
          <span className="hidden lg:inline">Search</span>
        </span>
        <span className="hidden lg:inline label-tag text-[10px]">⌘K</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl p-0 gap-0 max-h-[85vh] overflow-hidden flex flex-col">
          <div className="p-4 border-b border-[#E5E5E5] flex items-center gap-3">
            <MagnifyingGlass size={20} weight="duotone" className="text-[#525252] flex-shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search inquiries, remittances, statements…"
              className="flex-1 bg-transparent outline-none text-base"
              data-testid="search-input"
            />
            {loading && (
              <div className="w-4 h-4 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
            )}
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F5F5F5]">
              <X size={16} weight="bold" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {!q.trim() && (
              <div className="p-6 text-sm text-[#525252]">
                <div className="label-tag mb-2">Tips</div>
                Search by payer, invoice number, vendor, inquiry subject, or filename. Shortcut: <span className="font-mono font-bold">⌘K</span> / <span className="font-mono font-bold">/</span>
              </div>
            )}
            {q.trim() && results && total === 0 && !loading && (
              <div className="p-6 text-sm text-[#525252]">No matches for "{q}"</div>
            )}

            {results?.monthly_items?.length > 0 && (
              <div>
                <div className="label-tag px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                  <ListChecks size={12} weight="duotone" /> Monthly items
                </div>
                {results.monthly_items.map((i) => (
                  <button
                    key={i.id}
                    onClick={() => goto("/recurring")}
                    data-testid={`search-result-mi-${i.id}`}
                    className="w-full text-left px-4 py-3 border-b border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all"
                  >
                    <div className="text-sm font-medium">{i.name}</div>
                    <div className="label-tag text-[10px]">{i.month} · {i.status}</div>
                  </button>
                ))}
              </div>
            )}

            {results?.inquiries?.length > 0 && (
              <div>
                <div className="label-tag px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                  <ChatsCircle size={12} weight="duotone" /> Inquiries
                </div>
                {results.inquiries.map((q2) => (
                  <button
                    key={q2.id}
                    onClick={() => goto("/questions")}
                    data-testid={`search-result-inq-${q2.id}`}
                    className="w-full text-left px-4 py-3 border-b border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all"
                  >
                    <div className="text-sm font-medium">{q2.title}</div>
                    <div className="label-tag text-[10px]">{q2.status} · {new Date(q2.created_at).toLocaleDateString()}</div>
                  </button>
                ))}
              </div>
            )}

            {results?.remittances?.length > 0 && (
              <div>
                <div className="label-tag px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                  <Receipt size={12} weight="duotone" /> Remittances
                </div>
                {results.remittances.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => goto("/payment-advices")}
                    data-testid={`search-result-rem-${r.id}`}
                    className="w-full text-left px-4 py-3 border-b border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all"
                  >
                    <div className="text-sm font-medium">{r.payer_name || "—"}{r.amount ? ` · $${r.amount}` : ""}</div>
                    <div className="label-tag text-[10px]">{r.status} · {r.date || new Date(r.created_at).toLocaleDateString()} {r.invoice_number ? `· INV ${r.invoice_number}` : ""}</div>
                  </button>
                ))}
              </div>
            )}

            {results?.documents?.length > 0 && (
              <div>
                <div className="label-tag px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
                  <FileText size={12} weight="duotone" /> Documents
                </div>
                {results.documents.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => {
                      setOpen(false);
                      navigate("/files");
                    }}
                    data-testid={`search-result-doc-${d.id}`}
                    className="w-full text-left block px-4 py-3 border-b border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all"
                  >
                    <div className="text-sm font-medium truncate">{d.display_filename || d.original_filename}</div>
                    <div className="label-tag text-[10px]">{d.category} · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
